import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Coin } from "../components/ui/Coin.js";
import { Icon } from "../components/ui/Icon.js";
import {
  createAlert,
  deleteAlert,
  fetchAlerts,
  type PriceAlert,
} from "../lib/api.js";

const CONDITIONS: PriceAlert["condition"][] = [
  "CROSS_ABOVE",
  "CROSS_BELOW",
  "CHANGE_PCT_24H",
];

const EXCHANGES = ["binance", "bybit", "okx", "kraken", "coinbase"] as const;

function coinSym(s: string): string {
  return s.replace(/USDT$|USD$|USDC$/, "");
}

function fmtDate(ms: number | null): string {
  return ms ? new Date(ms).toLocaleString() : "—";
}

export function AlertsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["alerts"], queryFn: fetchAlerts });
  const [open, setOpen] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => deleteAlert(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Price Alerts</div>
          <div className="muted" style={{ fontSize: 11 }}>
            Threshold crossings and 24h % moves · email + in-app delivery
          </div>
        </div>
        <button
          type="button"
          className="se-btn se-btn-primary"
          style={{ marginLeft: "auto" }}
          onClick={() => setOpen(true)}
        >
          <Icon name="plus" size={13} /> New alert
        </button>
      </div>

      <div className="se-card">
        <table className="se-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Exchange</th>
              <th>Condition</th>
              <th>Threshold</th>
              <th>Notify</th>
              <th>Status</th>
              <th>Triggered</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(q.data ?? []).length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: 24, textAlign: "center" }} className="muted">
                  No alerts. Create one to be notified on a price threshold.
                </td>
              </tr>
            ) : (
              (q.data ?? []).map((a) => (
                <tr key={a.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Coin sym={coinSym(a.symbol)} size="sm" />
                      <span style={{ fontWeight: 500 }}>{a.symbol}</span>
                    </div>
                  </td>
                  <td className="muted">{a.exchange}</td>
                  <td>
                    <span className="se-tag">{a.condition.replace(/_/g, " ")}</span>
                  </td>
                  <td className="num">
                    {a.threshold}
                    {a.condition === "CHANGE_PCT_24H" ? "%" : ""}
                  </td>
                  <td className="muted" style={{ fontSize: 11 }}>
                    {[a.notifyEmail ? "email" : null, a.notifyPush ? "push" : null]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                  <td>
                    <span
                      className={`se-pill ${
                        a.status === "ACTIVE"
                          ? "se-pill-primary"
                          : a.status === "TRIGGERED"
                            ? "se-pill-buy"
                            : "se-pill-muted"
                      }`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="muted num" style={{ fontSize: 11 }}>{fmtDate(a.triggeredAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="se-btn se-btn-ghost se-btn-sm"
                      onClick={() => remove.mutate(a.id)}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {open ? (
        <NewAlertModal
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            qc.invalidateQueries({ queryKey: ["alerts"] });
          }}
        />
      ) : null}
    </div>
  );
}

function NewAlertModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [exchange, setExchange] = useState("binance");
  const [symbol, setSymbol] = useState("BTCUSDT");
  const [condition, setCondition] = useState<PriceAlert["condition"]>("CROSS_ABOVE");
  const [threshold, setThreshold] = useState(70000);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyPush, setNotifyPush] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await createAlert({
        exchange,
        symbol,
        condition,
        threshold,
        notifyEmail,
        notifyPush,
      });
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div className="se-card" style={{ width: 420, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>New price alert</div>

        <label className="muted" style={{ fontSize: 11, display: "block" }}>Exchange</label>
        <select className="se-input" value={exchange} onChange={(e) => setExchange(e.target.value)} style={{ marginBottom: 10 }}>
          {EXCHANGES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>

        <label className="muted" style={{ fontSize: 11, display: "block" }}>Symbol</label>
        <input className="se-input mono" value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} style={{ marginBottom: 10 }} />

        <label className="muted" style={{ fontSize: 11, display: "block" }}>Condition</label>
        <select className="se-input" value={condition} onChange={(e) => setCondition(e.target.value as PriceAlert["condition"])} style={{ marginBottom: 10 }}>
          {CONDITIONS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
        </select>

        <label className="muted" style={{ fontSize: 11, display: "block" }}>
          Threshold {condition === "CHANGE_PCT_24H" ? "(%)" : "(price)"}
        </label>
        <input
          className="se-input mono"
          type="number"
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
          style={{ marginBottom: 12 }}
        />

        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={notifyPush} onChange={(e) => setNotifyPush(e.target.checked)} /> In-app
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} /> Email
          </label>
        </div>

        {error ? <div style={{ color: "var(--sell)", fontSize: 11, marginBottom: 10 }}>{error}</div> : null}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="se-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="se-btn se-btn-primary" disabled={submitting} onClick={submit}>
            {submitting ? "Creating…" : "Create alert"}
          </button>
        </div>
      </div>
    </div>
  );
}
